/**
 * permitLimit.ts — the ONE shared permit-limit comparison + display helper.
 *
 * Stage 3 of ALPACA-Permit-To-Notification-v1.2. Ported VERBATIM (logic-for-logic)
 * from the package's tested prototype
 *   ALPACA-Permit-To-Notification-v1.2/3-Working-Prototype/Permit-Library-Prototype.html
 *   (op / opGlyph / outsideLimit / limitLine, lines 1441-1538)
 * so it is translated rather than re-derived — the highest-risk code in the build.
 *
 * GOVERNING RULE (spec §2, ADR-0012 D11): ALPACA compares, flags and notifies; it
 * NEVER determines regulatory compliance. `outsideLimit` is ARITHMETIC FOR THE
 * ANALYST'S EYE — a reading aid that may colour a cell red on the lab's own screen.
 * It must NEVER produce a Pass/Fail/Exceeded word, a status column, an export
 * column, or reach a client. The colour says WHERE to look, never WHAT it means.
 *
 * It returns `true` (outside), `false` (inside) or `null` (cannot tell — render
 * PLAIN, never guess): different units, an ambiguous censored value, or a
 * non-numeric result. A wrong red mark trains people to ignore red.
 *
 * ROW TINT (RTCFR "Permit Limit Row Color", owner amendment 2026-09-27): the result
 * grids ALSO tint the whole row — Pink (MCL reached) · Orange (MRL/AL/SMCL reached) ·
 * Yellow (detected above MDL) — via `permitRowTier` below. The owner deliberately
 * overrode ADR-0012 D11 / Batch-Results spec rules 180-189 and 238-243 for the ROW
 * TINT ONLY: three tiers, and a result AT the limit counts as reached (>=). It stays a
 * colour on the lab's own screen — never a word, a status column, an export column,
 * or anything a client sees. `outsideLimit` / the red limit line are unchanged.
 *
 * ALERT COLUMN (RTCFR "Display 'Notify lab' in Alert Column Across All Result Forms",
 * owner request 2026-10-13): the Alert column of REE / RT / RV / RA shows "⚑ Notify lab"
 * when ANY applicable limit is certainly violated — `permitAlertState` below, which is
 * exactly "some limit line is red" (`outsideLimit === true`), so the flag and the red
 * line can never disagree. A second owner-sanctioned exception to "never a word": the
 * text is an instruction to the analyst on the lab's own grid, not a compliance verdict,
 * and it sends nothing (notification e-mails stay with the Notification Center rules).
 */

/** One applicable permit limit for a sample-parameter (one line in the cell). */
export interface PermitLimit {
  /** Limit type name, printed as data — e.g. "MCL", "SMCL", "AL". No branch on it. */
  type: string;
  /** Operator — accepts the legacy XAF word ("LessThanEqual"), the two-char form
   *  ("<=") or the glyph ("≤"); all normalise identically so nothing stored migrates. */
  operator: string;
  /** Limit1 (the acceptable-condition value). */
  limit1: string | number | null | undefined;
  /** Limit2 — only used by the `Between` operator. */
  limit2?: string | number | null;
  /** Limit3 — lab-internal watch value; reference-only, NOT rendered in the line
   *  for now (open decision #3). Carried so the renderer can add it in one place later. */
  limit3?: string | number | null;
  /** The limit's own units (never converted — spec §4). */
  units?: string | null;
  /** Where the limit came from — "Permit" · "Global limit" · "Test parameter default".
   *  Shown in the cell tooltip, not as a column. */
  alertSource?: string | null;
  /** Whether this limit carries the Alert tick (PermitLibrary.AlertSubscribe). */
  alertTick?: boolean;
}

/** Accepts the glyph, the two-character form and the legacy XAF words so nothing
 *  already stored has to be migrated before this works. */
const OP_ALIAS: Record<string, string> = {
  "≤": "<=", "<=": "<=", lessthanequal: "<=",
  "≥": ">=", ">=": ">=", greaterthenequal: ">=", greaterthanequal: ">=",
  "<": "<", lessthan: "<",
  ">": ">", greaterthen: ">", greaterthan: ">",
  "=": "=", equal: "=",
  "≠": "≠", "<>": "≠", notequal: "≠",
  // "Inbetween" is the legacy XAF StoredValue the live data holds (AI_Operator seed row 7);
  // without it a Between limit (e.g. pH 6.5-8.5) was never evaluated (RTCFR 2026-10-13).
  between: "Between", inbetween: "Between",
  is: "Is", isnot: "Is Not", "is not": "Is Not",
};

/** Normalise any spelling of an operator to a canonical token. */
export function normalizeOperator(operator: string | null | undefined): string {
  const raw = String(operator ?? "").trim();
  return OP_ALIAS[raw.toLowerCase()] || OP_ALIAS[raw] || raw;
}

const OP_GLYPH: Record<string, string> = {
  "<=": "≤", ">=": "≥", "<": "<", ">": ">", "=": "=", "≠": "≠",
  Between: "Between", Is: "Is", "Is Not": "Is Not",
};

/** The single display glyph for an operator — so "GreaterThenEqual", ">=" and "≥"
 *  all render identically in every grid (spec §4). */
export function operatorGlyph(operator: string | null | undefined): string {
  const canon = normalizeOperator(operator);
  return OP_GLYPH[canon] || canon;
}

const normUnits = (u: string | null | undefined): string =>
  String(u ?? "").trim().toLowerCase().replace(/µ/g, "u");

/**
 * Is this result OUTSIDE the given limit?  true | false | null(cannot tell).
 * READ THE FILE HEADER BEFORE CHANGING THIS — it is a reading aid, never a verdict.
 *
 * @param limit     the applicable permit limit
 * @param raw       the reported result value, as entered/stored (string)
 * @param rawUnits  the result's own units (never converted to the limit's)
 */
export function outsideLimit(
  limit: PermitLimit | null | undefined,
  raw: string | number | null | undefined,
  rawUnits: string | null | undefined,
): boolean | null {
  const txt = String(raw ?? "").trim();
  if (!txt || !limit || limit.limit1 === undefined || limit.limit1 === null || limit.limit1 === "") {
    return null;
  }

  const o = operatorGlyph(limit.operator);

  // Qualitative — a word against a word (this is how "Is Absent" works).
  if (o === "Is") return txt.toLowerCase() !== String(limit.limit1).trim().toLowerCase();
  if (o === "Is Not") return txt.toLowerCase() === String(limit.limit1).trim().toLowerCase();

  // Units are NOT converted (D11), so they must MATCH to compare at all.
  // Comparing 20 ug/L against 0.015 mg/L unconverted invents a 1000x error.
  if (normUnits(rawUnits) !== normUnits(limit.units)) return null;

  const a = parseFloat(String(limit.limit1));
  const b = parseFloat(String(limit.limit2 ?? ""));
  const v = parseFloat(txt);

  // Censored results are decidable only when the qualifier fixes the direction.
  // "<1" against an upper bound of 500 is certainly inside; "<600" is unknowable.
  const cens = /^([<>])\s*([0-9.]+)$/.exec(txt);
  if (cens) {
    const dir = cens[1];
    const cv = parseFloat(cens[2] ?? "");
    const upper = o === "≤" || o === "<";
    if (dir === "<" && upper) return cv <= a ? false : null;
    if (dir === ">" && upper) return cv >= a ? true : null;
    return null;
  }
  if (Number.isNaN(v) || Number.isNaN(a)) return null;

  switch (o) {
    case "≤": return v > a;
    case "<": return v >= a;
    case "≥": return v < a;
    case ">": return v <= a;
    case "=": return v !== a;
    case "≠": return v === a;
    case "Between": return Number.isNaN(b) ? null : v < a || v > b;
    default: return null;
  }
}

/**
 * The display text for ONE limit line — one renderer for every type and operator.
 * "MCL ≤ 4.0 mg/L" · "SMCL Between 6.5 / 8.5 SU" · "MCL Is Absent P/A".
 * The limit TYPE is data, printed as-is — deliberately NO MCL/SMCL/AL branch.
 * Limit3 ("watch") is intentionally omitted for now (open decision #3); when it
 * lands it is a one-line suffix here, worded as a number ("· watch 8.0"), never a state.
 */
export function formatLimitLine(limit: PermitLimit): string {
  const g = operatorGlyph(limit.operator);
  const value = g === "Between" ? `${limit.limit1} / ${limit.limit2}` : String(limit.limit1 ?? "");
  const units = limit.units ? ` ${limit.units}` : "";
  return `${limit.type} ${g} ${value}${units}`.trim();
}

/** Separator between limits in the ONE-LINE Permit Limit cell ("MCL ≤ 200 ug/L · AL ≤ 150 ug/L"). */
export const PERMIT_LIMIT_SEPARATOR = " · ";

/** Every limit on ONE line, in the cell's order — the exact text the Permit Limit
 *  cell shows (used for its tooltip and for measuring the column width). */
export function formatLimitsInline(limits: PermitLimit[]): string {
  return limits.map(formatLimitLine).join(PERMIT_LIMIT_SEPARATOR);
}

/**
 * The ONE result value every form compares against its permit limits — the
 * reported result when present, else the numeric result — so the limit cell and
 * the row tint agree, and the same result tints the same on every form.
 */
export function permitResultValue(
  result: string | number | null | undefined,
  numeric: string | number | null | undefined,
): string {
  const reported = String(result ?? "").trim();
  return reported || String(numeric ?? "").trim();
}

/** Row-tint severity (highest first): MCL reached · MRL/AL/SMCL reached · detected above MDL. */
export type PermitRowTier = "mcl" | "action" | "detect";

/** Limit types per tier. Type names are data — matched trimmed + case-insensitive.
 *  SMAL is honoured if a lab ever configures it; nothing creates it. Any other type
 *  (Daily Maximum, Monthly Average, Minimum, Maximum, …) never tints a row. */
const MCL_TYPES = new Set(["mcl"]);
const ACTION_TYPES = new Set(["mrl", "al", "smcl", "smal"]);
const MDL_TYPES = new Set(["mdl"]);

const typeKey = (t: string | null | undefined): string => String(t ?? "").trim().toLowerCase();

/** A plain finite number, or NaN — never a partial parse ("5abc" is not 5). */
const toNumber = (v: string | number | null | undefined): number => {
  const s = String(v ?? "").trim();
  return s !== "" && Number.isFinite(Number(s)) ? Number(s) : Number.NaN;
};

/**
 * Has this result REACHED the limit?  true | false | null(cannot tell → ignore).
 * Upper-bound limits (≤, <) are reached AT the limit (result >= Limit1, owner
 * amendment 2026-09-27); every other operator reuses `outsideLimit`.
 */
function limitReached(
  limit: PermitLimit,
  raw: string,
  rawUnits: string | null | undefined,
): boolean | null {
  if (!raw || limit.limit1 === undefined || limit.limit1 === null || limit.limit1 === "") return null;
  const o = operatorGlyph(limit.operator);
  if (o !== "≤" && o !== "<") return outsideLimit(limit, raw, rawUnits);

  if (normUnits(rawUnits) !== normUnits(limit.units)) return null;
  const a = toNumber(limit.limit1);
  if (Number.isNaN(a)) return null;

  const cens = /^([<>])\s*([0-9.]+)$/.exec(raw);
  if (cens) {
    const cv = toNumber(cens[2]);
    if (cens[1] === "<") return false;           // below a reporting value — never "reached"
    return cv >= a ? true : null;                // ">6" vs 5 is certainly reached; ">3" is unknowable
  }
  const v = toNumber(raw);
  return Number.isNaN(v) ? null : v >= a;
}

/**
 * The row tint for one result, from its parameter's permit limits
 * (RTCFR "Permit Limit Row Color", owner amendment 2026-09-27):
 *   Pink   `mcl`    — any MCL limit reached (highest priority)
 *   Orange `action` — any MRL / AL / SMCL limit reached
 *   Yellow `detect` — result > MDL (a permit "MDL" limit if configured, else the row's own MDL)
 *   none   `undefined` — the grid's default row colour
 * Missing limit types are simply absent — never treated as 0. A unit mismatch, a
 * non-numeric result or an ambiguous censored value never tints (cannot tell).
 *
 * @param limits  the row's parsed PermitLimitsJson (undefined → no permit)
 * @param result  the value from `permitResultValue`
 * @param units   the result's own units
 * @param rowMdl  the row's own MDL (SampleParameter.MDL → Testparameter.MDL)
 */
export function permitRowTier(
  limits: PermitLimit[] | undefined,
  result: string | number | null | undefined,
  units: string | null | undefined,
  rowMdl: string | number | null | undefined,
): PermitRowTier | undefined {
  const raw = String(result ?? "").trim();
  if (!raw) return undefined;
  const list = limits ?? [];

  const reachedAny = (types: Set<string>): boolean =>
    list.some((l) => types.has(typeKey(l.type)) && limitReached(l, raw, units) === true);

  if (reachedAny(MCL_TYPES)) return "mcl";
  if (reachedAny(ACTION_TYPES)) return "action";

  // Detection needs a permit (standing scope: permit-driven highlighting) and an MDL.
  if (list.length === 0) return undefined;
  const permitMdl = list.find((l) => MDL_TYPES.has(typeKey(l.type)));
  if (permitMdl && normUnits(permitMdl.units) && normUnits(permitMdl.units) !== normUnits(units)) {
    return undefined;
  }
  const mdl = toNumber(permitMdl ? permitMdl.limit1 : rowMdl);
  if (Number.isNaN(mdl)) return undefined;
  if (/^</.test(raw)) return undefined;          // censored "<x" — not detected
  const gt = /^>\s*([0-9.]+)$/.exec(raw);
  const v = toNumber(gt ? gt[1] : raw);
  if (Number.isNaN(v)) return undefined;
  return v > mdl || (gt !== null && v >= mdl) ? "detect" : undefined;
}

/** The CSS class for a tier (permitLimit.css) — undefined keeps the default row colour. */
export function permitRowClass(tier: PermitRowTier | undefined): string | undefined {
  return tier ? `plim-row-${tier}` : undefined;
}

/** True when the Result column holds data. null / "" / whitespace = NO result;
 *  a real 0 (or "0") IS a result and is evaluated normally. */
export function hasResult(result: string | number | null | undefined): boolean {
  return String(result ?? "").trim() !== "";
}

/**
 * One call per grid row: value picking (`permitResultValue`) + tier + class, so
 * every form wires the row tint identically and cannot drift.
 *
 * RTCFR "Default Row Color When Result Is Empty" (2026-09-27): a row with NO result —
 * BOTH Result and NumericResult null / empty / blank — keeps the grid's own default
 * colour (zebra, hover, selected — no class is added, so nothing is hardcoded).
 * NumericResult counts as a result too (owner decision 2026-09-27): if either holds
 * data the row is compared against MDL / MRL / AL / SMCL / MCL, using the reported
 * Result when present, else NumericResult (`permitResultValue`).
 */
export function permitRowClassFor(
  limits: PermitLimit[] | undefined,
  result: string | number | null | undefined,
  numeric: string | number | null | undefined,
  units: string | null | undefined,
  rowMdl: string | number | null | undefined,
): string | undefined {
  if (!hasResult(result) && !hasResult(numeric)) return undefined;
  return permitRowClass(permitRowTier(limits, permitResultValue(result, numeric), units, rowMdl));
}

/** Alert-column state for one result row:
 *  `notify` — at least one applicable limit is certainly violated (its line is red);
 *  `clear`  — the row has limits and a result, and none is violated;
 *  `none`   — no permit limits, or no result (Result AND NumericResult blank). */
export type PermitAlertState = "notify" | "clear" | "none";

/** The Alert-column text (owner wording, RTCFR 2026-10-13). */
export const PERMIT_ALERT_TEXT = "Notify lab";
/** What the Alert column shows when the row has limits and a result but violates none. */
export const PERMIT_ALERT_CLEAR = "—";

/**
 * Does this row raise the "Notify lab" alert? Every configured limit type and
 * operator counts (≤ < ≥ > = ≠ Between Is / Is Not, via `outsideLimit`), with the same
 * unit rule (units must match — never converted) and the same censored-value rule
 * ("<x" / ">x" only when certain). Uses the same value picker as the cell and row tint
 * (`permitResultValue` — Result, else NumericResult), so all three always agree.
 */
export function permitAlertState(
  limits: PermitLimit[] | undefined,
  result: string | number | null | undefined,
  numeric: string | number | null | undefined,
  units: string | null | undefined,
): PermitAlertState {
  if (!limits || limits.length === 0) return "none";
  if (!hasResult(result) && !hasResult(numeric)) return "none";
  const value = permitResultValue(result, numeric);
  return limits.some((l) => outsideLimit(l, value, units) === true) ? "notify" : "clear";
}

/** The Alert column's plain text (export / column filter / search) for a row —
 *  "Notify lab", "—", or the row's own Alert value when it has no limits/result. */
export function permitAlertText(
  limits: PermitLimit[] | undefined,
  result: string | number | null | undefined,
  numeric: string | number | null | undefined,
  units: string | null | undefined,
  fallback: string | null | undefined,
): string {
  const state = permitAlertState(limits, result, numeric, units);
  return state === "notify" ? PERMIT_ALERT_TEXT : state === "clear" ? PERMIT_ALERT_CLEAR : (fallback ?? "");
}

/**
 * Parse the reader's `PermitLimitsJson` (a FOR JSON PATH array, or null) into
 * typed limits. Never throws — a bad/blank payload yields `undefined` so the
 * caller falls back to its scalar value and flags nothing.
 */
export function parsePermitLimits(json: string | null | undefined): PermitLimit[] | undefined {
  if (!json) return undefined;
  try {
    const arr = JSON.parse(json) as PermitLimit[];
    return Array.isArray(arr) && arr.length > 0 ? arr : undefined;
  } catch {
    return undefined;
  }
}
